import {
  Background,
  BackgroundVariant,
  ControlButton,
  Controls,
  Panel,
  ReactFlow,
  Position,
  SelectionMode,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useStoreApi,
  useUpdateNodeInternals,
  type Connection,
  type Edge,
  type FinalConnectionState,
  type FitViewOptions,
  type Node,
  type OnSelectionChangeParams,
  type Viewport,
} from '@xyflow/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { SwimlaneSummary } from '@flowx/designer/modules/core/dictionary/swimlane.dictionary'
import {
  CANVAS_CONTROLS_STYLE,
  LAYOUT_TOOL_BUTTON_CLASS_NAME,
  LAYOUT_TOOLS_PANEL_STYLE,
  LayoutPreviewBar,
  layoutPreviewSummary,
  useLayoutPreviewKeys,
} from '@flowx/designer/shared/react-flow/layout-preview'

import { CanvasData } from '../../bpmn-canvas.types'
import { Point } from '../../dictionary/geometry.types'
import { GraphColors, GRID_SNAP_SIZE } from '../../bpmn-graph.constants'

import {
  BOUNDARY_EVENT_NODE_TYPE,
  BPMN_NODE_TYPE,
  BoundaryEventNodeData,
  BpmnNodeData,
  canAddOutgoingSequence,
  computeLaneResizeBounds,
  findNavAreaAt,
  findSwimlaneAt,
  isRectInsideSomeLane,
  overlapsAnyNode,
  resolveConnectionTarget,
  resolveSequenceSource,
  SEQUENCE_EDGE_TYPE,
  SequenceEdgeData,
  sequenceValidationMessage,
  snapToNodeBorder,
  stretchNavAreaNodes,
  validateSequenceConnection,
  withInitialSequencePoints,
  toAbsolutePosition,
  toBoundaryId,
  toFlowGraph,
  toNavAreaNodes,
  toNodeId,
} from './bpmn-graph.mapper'
import { BpmnDiagramProps, NavAreaRender } from './bpmn-diagram.types'
import {
  edgeTypes,
  FIT_VIEW_MAX_ZOOM,
  FIT_VIEW_PADDING,
  MAX_ZOOM,
  MIN_ZOOM,
  nodeTypes,
} from './canvas-config'
import { useCssZoomCompensation } from './css-zoom-compensation'
import { CssZoomContext } from './css-zoom.context'
import { computeLiveDragRects, pointsEqual, routeForEndpointRects } from './drag-geometry.util'
import { ConnectionTargetContext, ConnectionTargetResolver } from './connection-target.context'
import { DragOverEdgeContext } from './edge-drag.context'
import { EditingUsersContext } from './editing-users.context'
import { targetHandlePosition } from './target-handles'
import { snapGroupCenterToGrid, snapPointToGrid } from './grid-snapping.util'
import { planBpmnMultiselection } from './multiselection.util'
import {
  ProcessLayoutPlan,
  applyLayoutPlan,
  planProcessLayout,
  showsLayoutPlan,
} from './process-auto-layout.util'
import {
  commitNodeDrop,
  NodeDropCommit,
  planNodeDrop,
  requiresAreaRemovalConfirm,
  revertCrossLaneNodeDrop,
} from './node-drop-plan.util'
import { SwimlaneUiContext, SwimlaneUiState } from './swimlane-ui.context'
import {
  canvasContainerRect,
  nodeViewportRect,
  pointerViewportPoint,
  projectedViewportRect,
} from './viewport-coords.util'
import { useNewNodePanel } from './use-new-node-panel'
import { usePaletteDnd } from './use-palette-dnd'
import PaletteDropPreview from './components/PaletteDropPreview'
import SequenceConnectionLine from './edges/SequenceConnectionLine'
import { EdgeRect } from './edges/orthogonal-edge.util'
import { BOUNDARY_DIMENSIONS, getNodeDimensions } from './node-visuals'

const isEmptyLayoutPlan = ({ nodes, swimlanes, sequences }: ProcessLayoutPlan): boolean =>
  !nodes.length && !swimlanes.length && !sequences.length

interface SequenceDragOrigin {
  edge: Edge<SequenceEdgeData>
  points: Point[]
  sourceRect: EdgeRect
  targetRect: EdgeRect
}

interface NodeSequenceDrag {
  /** Nodes protected from inbound snapshots (dragged nodes + moving boundary children). */
  draggedIds: Set<string>
  actualDraggedIds: Set<string>
  endpointIds: Set<string>
  nodeRects: Map<string, EdgeRect>
  liveRects: Map<string, EdgeRect>
  origins: Map<string, SequenceDragOrigin>
  stopHandled: boolean
}

interface PendingNodeLayout {
  position: Point
  parentId?: string
}

export default function BpmnFlowCanvas(props: BpmnDiagramProps) {
  const {
    data: liveData,
    readonly: liveReadonly,
    allowReadOnly,
    highlightedNodeId,
    navAreas,
    navAreasLaneId,
    paletteDragType,
    panTarget,
    fitViewKey,
    bottomPanelHeightPc,
    editingUsersByNode,
    aiDraftPreview,
    navDisabledNodeIds,
    pastedNodeIds,
    onNodesPositionChanged,
    onNodesSwimlaneChanged,
    onNodesDragging,
    onBoundaryEventPositionChanged,
    onOpenConfigPanel,
    onNodeRightClick,
    onSequenceClick,
    onCanvasInteraction,
    onViewportChange,
    onPaneClicked,
    onSwimlaneRightClick,
    onMultiSelection,
    onMultiSelectionCleared,
    onSwimlaneResize,
    onNavAreaClick,
    onConfirmAreaRemoval,
    onPaletteDrop,
    onNodeDrop,
    onDropError,
    onSequenceDrop,
    onSequenceCreate,
    onSequencesChanged,
    onNodeCreateWithSequence,
    onSwimlaneAdd,
    onSwimlaneMove,
    onSwimlaneDelete,
    onSwimlaneRename,
    onConfirmAutoArrange,
    onAutoLayoutApplied,
    onLayoutPreviewChange,
  } = props

  // Auto-arrange first shows its result as a read-only preview; nothing is saved until Apply.
  // While it is open the whole canvas renders the previewed data ("Show current" flips back to
  // the live data) and is read-only, so every interaction guard below applies to it unchanged.
  const [layoutPreview, setLayoutPreview] = useState<{
    source: CanvasData
    plan: ProcessLayoutPlan
  } | null>(null)
  const [showCurrent, setShowCurrent] = useState(false)
  const previewPlan = layoutPreview?.plan ?? null
  const previewData = useMemo(
    () => layoutPreview && previewPlan && applyLayoutPlan(layoutPreview.source, previewPlan),
    [layoutPreview, previewPlan]
  )
  const data = previewData && !showCurrent ? previewData : liveData
  const readonly = liveReadonly || !!layoutPreview
  const layoutPreviewOpen = !!layoutPreview
  useEffect(() => {
    if (!layoutPreviewOpen) {
      return
    }
    onLayoutPreviewChange(true)

    return () => onLayoutPreviewChange(false)
  }, [layoutPreviewOpen, onLayoutPreviewChange])

  const {
    screenToFlowPosition,
    flowToScreenPosition,
    setCenter,
    getViewport,
    setViewport,
    fitView,
  } = useReactFlow()
  const flowStoreApi = useStoreApi()
  const updateNodeInternals = useUpdateNodeInternals()
  const { cssZoom, reactFlowRef, reactFlowStyle, toLogicalViewport } = useCssZoomCompensation(
    MIN_ZOOM,
    MAX_ZOOM
  )
  const appliedPanRequestId = useRef(0)
  const [selectedLaneId, setSelectedLaneId] = useState<SwimlaneSummary['id'] | null>(null)

  const orderedLaneIds = useMemo(
    () =>
      [...data.swimlanes]
        .sort((a, b) => (a.layoutOptions?.y ?? 0) - (b.layoutOptions?.y ?? 0))
        .map((swimlane) => swimlane.id),
    [data.swimlanes]
  )

  const laneResizeBounds = useMemo(() => computeLaneResizeBounds(data, getNodeDimensions), [data])

  const swimlaneUi = useMemo<SwimlaneUiState>(
    () => ({
      selectedLaneId,
      readonly,
      orderedLaneIds,
      resizeBounds: laneResizeBounds,
      selectLane: (id) => setSelectedLaneId(layoutPreviewOpen ? null : id),
      addLane: (swimlane, direction) => {
        setSelectedLaneId(null)
        onSwimlaneAdd({ swimlane, direction })
      },
      moveLane: (swimlane, direction) => {
        onSwimlaneMove({ swimlane, direction })
      },
      deleteLane: (swimlane) => {
        setSelectedLaneId(null)
        onSwimlaneDelete({ swimlane })
      },
      renameLane: (swimlane, newName) => {
        onSwimlaneRename({ swimlane, newName })
      },
      resizeLane: (swimlane, layoutOptions) => {
        onSwimlaneResize({ swimlane, layoutOptions })
      },
    }),
    [
      selectedLaneId,
      readonly,
      layoutPreviewOpen,
      orderedLaneIds,
      laneResizeBounds,
      onSwimlaneAdd,
      onSwimlaneMove,
      onSwimlaneDelete,
      onSwimlaneRename,
      onSwimlaneResize,
    ]
  )

  const graph = useMemo(() => {
    const base = toFlowGraph(data, {
      readonly,
      highlightedNodeId,
      allowReadOnly,
      aiDraftPreview,
      navDisabledNodeIds,
      pastedNodeIds,
    })

    // Nav-area overlays render as extra lane children; appended after the base
    // nodes so their parent lane always precedes them in the array.
    return {
      nodes: [...base.nodes, ...toNavAreaNodes(navAreas ?? [], navAreasLaneId)],
      edges: base.edges,
    }
  }, [
    data,
    readonly,
    highlightedNodeId,
    allowReadOnly,
    aiDraftPreview,
    navDisabledNodeIds,
    pastedNodeIds,
    navAreas,
    navAreasLaneId,
  ])

  const [nodes, setNodes, onNodesChange] = useNodesState(graph.nodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(graph.edges)
  // Nothing can be selected while a layout preview is open: drop what was selected before it
  // opened, or a selected sequence would keep its toolbar and a node its actions
  useEffect(() => {
    if (!layoutPreviewOpen) {
      return
    }
    setSelectedLaneId(null)
    setNodes((previous) =>
      previous.some((node) => node.selected)
        ? previous.map((node) => (node.selected ? { ...node, selected: false } : node))
        : previous
    )
    setEdges((previous) =>
      previous.some((edge) => edge.selected)
        ? previous.map((edge) => (edge.selected ? { ...edge, selected: false } : edge))
        : previous
    )
  }, [layoutPreviewOpen, setEdges, setNodes])
  const graphRef = useRef(graph)
  graphRef.current = graph
  const nodeSequenceDrag = useRef<NodeSequenceDrag | null>(null)
  const cancelledNodeDragIds = useRef(new Set<string>())
  const pendingSequencePoints = useRef(new Map<string, Point[]>())
  const pendingNodeLayouts = useRef(new Map<string, PendingNodeLayout>())
  const pendingCommitTimeouts = useRef(new Set<number>())
  const multiselectionReported = useRef(false)

  useEffect(
    () => () => {
      pendingCommitTimeouts.current.forEach((timeout) => window.clearTimeout(timeout))
      pendingCommitTimeouts.current.clear()
    },
    []
  )

  const internalRect = useCallback(
    (nodeId: string): EdgeRect | null => {
      const internal = flowStoreApi.getState().nodeLookup.get(nodeId)
      const width = internal?.measured?.width
      const height = internal?.measured?.height
      if (!internal || !width || !height) {
        return null
      }

      return {
        x: internal.internals.positionAbsolute.x,
        y: internal.internals.positionAbsolute.y,
        width,
        height,
      }
    },
    [flowStoreApi]
  )

  const snapDraggedNodesToGrid = useCallback(
    (draggedNodes: Node[]): Node[] => {
      const bpmnItems = draggedNodes
        .filter((node) => node.type === BPMN_NODE_TYPE)
        .map((node) => {
          const definition = (node.data as BpmnNodeData).definition
          const parentRect = node.parentId ? internalRect(node.parentId) : null

          return {
            id: node.id,
            position: node.position,
            parentPosition: { x: parentRect?.x ?? 0, y: parentRect?.y ?? 0 },
            ...getNodeDimensions(definition.type),
          }
        })
      const bpmnPositions = snapGroupCenterToGrid(bpmnItems, GRID_SNAP_SIZE)

      return draggedNodes.map((node) => {
        const bpmnPosition = bpmnPositions.get(node.id)
        if (bpmnPosition) {
          return { ...node, position: bpmnPosition }
        }

        // Boundary events retain the old top-left grid behavior before their
        // final border snap. Other node kinds are not draggable.
        const parentRect = node.parentId ? internalRect(node.parentId) : null
        const parentPosition = { x: parentRect?.x ?? 0, y: parentRect?.y ?? 0 }
        const absolute = snapPointToGrid(
          {
            x: parentPosition.x + node.position.x,
            y: parentPosition.y + node.position.y,
          },
          GRID_SNAP_SIZE
        )

        return {
          ...node,
          position: {
            x: absolute.x - parentPosition.x,
            y: absolute.y - parentPosition.y,
          },
        }
      })
    },
    [internalRect]
  )

  const applyLocalDraggedPositions = useCallback(
    (draggedNodes: Node[]): void => {
      const positions = new Map(draggedNodes.map((node) => [node.id, node.position]))
      setNodes((previous) =>
        previous.map((node) => {
          const position = positions.get(node.id)

          return position && (position.x !== node.position.x || position.y !== node.position.y)
            ? { ...node, position }
            : node
        })
      )
    },
    [setNodes]
  )

  const holdNodeLayoutUntilCanonical = useCallback(
    (nodeId: string, layout: PendingNodeLayout) => {
      pendingNodeLayouts.current.set(nodeId, layout)
      const expire = () => {
        if (pendingNodeLayouts.current.get(nodeId) !== layout) {
          return
        }
        if (nodeSequenceDrag.current?.draggedIds.has(nodeId)) {
          schedule()

          return
        }
        pendingNodeLayouts.current.delete(nodeId)
        const canonical = graphRef.current.nodes.find((node) => node.id === nodeId)
        setNodes((previous) => {
          const existing = previous.find((node) => node.id === nodeId)
          if (!canonical) {
            return previous.filter((node) => node.id !== nodeId)
          }

          return previous.map((node) =>
            node.id === nodeId
              ? {
                  ...canonical,
                  ...(existing?.measured?.width ? { measured: existing.measured } : {}),
                  ...(existing?.selected ? { selected: true } : {}),
                }
              : node
          )
        })
      }
      const schedule = () => {
        const timeout = window.setTimeout(() => {
          pendingCommitTimeouts.current.delete(timeout)
          expire()
        }, 30000)
        pendingCommitTimeouts.current.add(timeout)
      }
      schedule()
    },
    [setNodes]
  )

  const holdSequencePointsUntilCanonical = useCallback(
    (edgeId: string, points: Point[]) => {
      pendingSequencePoints.current.set(edgeId, points)
      const expire = () => {
        if (pendingSequencePoints.current.get(edgeId) !== points) {
          return
        }
        if (nodeSequenceDrag.current?.origins.has(edgeId)) {
          schedule()

          return
        }
        pendingSequencePoints.current.delete(edgeId)
        const canonical = graphRef.current.edges.find((edge) => edge.id === edgeId)
        setEdges((previous) => {
          if (!canonical) {
            return previous.filter((edge) => edge.id !== edgeId)
          }
          const selected = previous.find((edge) => edge.id === edgeId)?.selected

          return previous.map((edge) =>
            edge.id === edgeId
              ? {
                  ...canonical,
                  ...(selected ? { selected: true } : {}),
                }
              : edge
          )
        })
      }
      const schedule = () => {
        const timeout = window.setTimeout(() => {
          pendingCommitTimeouts.current.delete(timeout)
          expire()
        }, 5000)
        pendingCommitTimeouts.current.add(timeout)
      }
      schedule()
    },
    [setEdges]
  )

  const [arranging, setArranging] = useState(false)
  const [applying, setApplying] = useState(false)
  // Apply is waiting on the collaborator confirmation: the preview bar and its keys stay idle so
  // neither a second Apply nor a Cancel can slip in behind the modal
  const [confirming, setConfirming] = useState(false)
  const confirmingRef = useRef(false)
  const liveDataRef = useRef(liveData)
  liveDataRef.current = liveData
  const layoutPreviewRef = useRef(layoutPreview)
  layoutPreviewRef.current = layoutPreview
  const viewportBeforePreview = useRef<Viewport | null>(null)
  const applyFallbackTimeout = useRef<number | null>(null)
  // Someone (a collaborator, an autosave) changed the process after the preview was planned
  const previewStale = !!layoutPreview && !applying && liveData !== layoutPreview.source

  const closeLayoutPreview = useCallback(
    (restoreViewport: boolean) => {
      if (applyFallbackTimeout.current !== null) {
        window.clearTimeout(applyFallbackTimeout.current)
        applyFallbackTimeout.current = null
      }
      setLayoutPreview(null)
      setShowCurrent(false)
      setApplying(false)
      if (restoreViewport && viewportBeforePreview.current) {
        void setViewport(viewportBeforePreview.current, { duration: 300 })
      }
      viewportBeforePreview.current = null
    },
    [setViewport]
  )

  /**
   * Plans the layout afresh from the live diagram. A refresh of an open preview is dropped when
   * the preview is closed (Escape) while it plans.
   */
  const planLayoutPreview = useCallback(
    async (refresh = false) => {
      setArranging(true)
      try {
        const source = liveDataRef.current
        const plan = await planProcessLayout(source)
        setShowCurrent(false)
        setLayoutPreview((current) => (refresh && !current ? current : { source, plan }))
      } catch (error) {
        // Layout itself failed (ELK chunk did not load, invalid graph); nothing was changed
        console.error('Process auto-layout failed', error)
        onDropError('The process could not be arranged')
      } finally {
        setArranging(false)
      }
    },
    [onDropError]
  )

  const autoArrange = useCallback(() => {
    if (liveReadonly || arranging || layoutPreview) {
      return
    }
    viewportBeforePreview.current = getViewport()
    void planLayoutPreview()
  }, [arranging, getViewport, layoutPreview, liveReadonly, planLayoutPreview])

  // The confirmation asked for when collaborators are editing comes at Apply — after the user has
  // seen what would change — and the preview must still match the live data once it is answered.
  const applyLayoutPreview = useCallback(async () => {
    if (
      !layoutPreview ||
      !previewPlan ||
      previewStale ||
      applying ||
      arranging ||
      liveReadonly ||
      confirmingRef.current
    ) {
      return
    }
    if (isEmptyLayoutPlan(previewPlan)) {
      closeLayoutPreview(false)
      return
    }
    confirmingRef.current = true
    setConfirming(true)
    let confirmed: boolean
    try {
      confirmed = await onConfirmAutoArrange()
    } finally {
      confirmingRef.current = false
      setConfirming(false)
    }
    // The preview must still be the one that was confirmed, over the same live data
    if (
      !confirmed ||
      layoutPreviewRef.current !== layoutPreview ||
      liveDataRef.current !== layoutPreview.source
    ) {
      return
    }
    setShowCurrent(false)
    setApplying(true)
    if (!(await onAutoLayoutApplied(previewPlan))) {
      closeLayoutPreview(false)
      return
    }
    // Normally the preview closes as soon as the live data shows the saved layout (effect below);
    // this only guards against never seeing an exact match
    applyFallbackTimeout.current = window.setTimeout(() => closeLayoutPreview(false), 3000)
  }, [
    applying,
    arranging,
    closeLayoutPreview,
    layoutPreview,
    liveReadonly,
    onAutoLayoutApplied,
    onConfirmAutoArrange,
    previewPlan,
    previewStale,
  ])

  useEffect(() => {
    if (applying && previewPlan && showsLayoutPlan(liveData, previewPlan)) {
      closeLayoutPreview(false)
    }
  }, [applying, closeLayoutPreview, liveData, previewPlan])

  const cancelLayoutPreview = useCallback(() => closeLayoutPreview(true), [closeLayoutPreview])
  const confirmLayoutPreview = useCallback(() => void applyLayoutPreview(), [applyLayoutPreview])
  useLayoutPreviewKeys(!!layoutPreview && !applying && !confirming, {
    onApply: confirmLayoutPreview,
    onCancel: cancelLayoutPreview,
  })

  // Keeping the hook's members separate preserves the callback memoization of
  // every handler that only needs one of them.
  const {
    graph: newNodePanelGraph,
    open: openNewNodePanel,
    dismissOnPaneClick: dismissNewNodePanelOnPaneClick,
  } = useNewNodePanel({
    data,
    readonly,
    graphNodes: graph.nodes,
    onDropError,
    onNodeCreateWithSequence,
  })

  useEffect(() => {
    setNodes((previous) => {
      const previousById = new Map(previous.map((node) => [node.id, node]))

      // Carry React Flow's own bookkeeping over to the fresh node objects:
      // `measured` especially — without it React Flow re-renders every node
      // `visibility: hidden` until re-measured, and when two syncs land close
      // together (e.g. a sequence delete updating effector + NgRx) the second
      // wipe gets no fresh dimensions event, leaving all nodes invisible to
      // hit-testing — no connections, drags or clicks — until the next render.
      const rebuilt = graph.nodes.map((node) => {
        const previousNode = previousById.get(node.id)
        const locallyDragged = nodeSequenceDrag.current?.draggedIds.has(node.id)
        const pending = pendingNodeLayouts.current.get(node.id)
        if (
          pending &&
          pending.position.x === node.position.x &&
          pending.position.y === node.position.y &&
          pending.parentId === node.parentId
        ) {
          pendingNodeLayouts.current.delete(node.id)
        }
        const effectivePending = pendingNodeLayouts.current.get(node.id)

        return {
          ...node,
          ...(previousNode?.measured?.width ? { measured: previousNode.measured } : {}),
          ...(previousNode?.selected ? { selected: true } : {}),
          ...(locallyDragged && previousNode
            ? { position: previousNode.position, parentId: previousNode.parentId }
            : effectivePending
              ? { position: effectivePending.position, parentId: effectivePending.parentId }
              : {}),
        }
      })

      const rebuiltIds = new Set(rebuilt.map((node) => node.id))
      const activelyDeleted = previous.filter(
        (node) => nodeSequenceDrag.current?.draggedIds.has(node.id) && !rebuiltIds.has(node.id)
      )
      const synchronized = [...rebuilt, ...activelyDeleted]

      // The New Node panel isn't produced by the mapper — append it so graph
      // re-emits (autosaves, collaborator edits) don't dismiss an open panel.
      return newNodePanelGraph ? [...synchronized, newNodePanelGraph.node] : synchronized
    })
    setEdges((previous) => {
      // Preserve selection and any active/pending local sequence geometry while
      // canonical props cross the Angular bridge. Inbound collaboration updates
      // touching an active gesture are therefore deferred until it ends.
      const previousById = new Map(previous.map((edge) => [edge.id, edge]))
      const selectedIds = new Set(previous.filter((edge) => edge.selected).map((edge) => edge.id))
      const rebuilt = graph.edges.map((edge) => {
        const active = nodeSequenceDrag.current?.origins.has(edge.id)
        const previousEdge = previousById.get(edge.id)
        if (active && previousEdge) {
          return {
            ...edge,
            ...(previousEdge.selected ? { selected: true } : {}),
            data: {
              ...edge.data,
              points: (previousEdge.data as SequenceEdgeData).points,
            },
          }
        }
        const pending = pendingSequencePoints.current.get(edge.id)
        const graphPoints = (edge.data as SequenceEdgeData).points
        if (pending && pointsEqual(pending, graphPoints)) {
          pendingSequencePoints.current.delete(edge.id)
        }
        const effective = pending ?? graphPoints
        const withEffectivePoints =
          effective === graphPoints ? edge : { ...edge, data: { ...edge.data, points: effective } }

        return selectedIds.has(edge.id)
          ? { ...withEffectivePoints, selected: true }
          : withEffectivePoints
      })

      const rebuiltIds = new Set(rebuilt.map((edge) => edge.id))
      const activelyDeleted = previous.filter(
        (edge) => nodeSequenceDrag.current?.origins.has(edge.id) && !rebuiltIds.has(edge.id)
      )

      return [
        ...rebuilt,
        ...activelyDeleted,
        ...(newNodePanelGraph ? [newNodePanelGraph.edge] : []),
      ]
    })
  }, [graph, setNodes, setEdges, newNodePanelGraph])

  // Heal nodes React Flow never measured: a data sync landing between a node's
  // mount and its first ResizeObserver tick (the NgRx echo right after a
  // delete/create) wipes the freshly computed internals, and the observer never
  // re-fires for an unchanged size — the node keeps `measured: {}`, gets no
  // handleBounds, and is invisible to hit-testing and connection drops
  // (sequences to it uncreatable). Runs post-commit, so the DOM exists; settles
  // once the dimension changes land back in the nodes state.
  useEffect(() => {
    const unmeasured = nodes.filter((node) => !node.measured?.width).map((node) => node.id)
    if (unmeasured.length) {
      updateNodeInternals(unmeasured)
    }
  }, [nodes, updateNodeInternals])

  // Pan to a selection: center the rect at the
  // current zoom, shifted up by half the options-panel height so the target lands
  // in the visible strip above the panel. Applied on requestId change and again on
  // init — a deep-link request can arrive before React Flow's viewport is ready.
  const applyPanTarget = useCallback(() => {
    if (!panTarget || panTarget.requestId === appliedPanRequestId.current) {
      return
    }
    appliedPanRequestId.current = panTarget.requestId
    const { zoom } = getViewport()
    const { height } = flowStoreApi.getState()
    const { rect, offsetHeightPc } = panTarget
    setCenter(
      rect.x + rect.width / 2,
      rect.y + rect.height / 2 + (height * offsetHeightPc) / 2 / zoom,
      { zoom }
    )
  }, [panTarget, getViewport, setCenter, flowStoreApi])

  useEffect(() => {
    applyPanTarget()
  }, [applyPanTarget])

  // Fitting reserves whatever the host tells us is hidden behind its bottom panel,
  // so the diagram lands in the strip that is actually on screen. The reserve is
  // a `%` string because
  // React Flow reads a bare padding number as a share of the *graph* to leave
  // around it, not as a share of the container we need to keep clear.
  const fitViewOptions = useMemo<FitViewOptions>(
    () => ({
      maxZoom: FIT_VIEW_MAX_ZOOM * cssZoom,
      padding: bottomPanelHeightPc
        ? {
            top: FIT_VIEW_PADDING,
            left: FIT_VIEW_PADDING,
            right: FIT_VIEW_PADDING,
            bottom: `${bottomPanelHeightPc * 100}%`,
          }
        : FIT_VIEW_PADDING,
    }),
    [cssZoom, bottomPanelHeightPc]
  )

  // React Flow's `fitView` prop resolves once, against whatever the canvas holds
  // when its first nodes get measured — an empty graph included, and the instance
  // viewer mounts this canvas before its process has loaded. Hosts that own more
  // than one diagram pass `fitViewKey`, and each new value queues another fit,
  // which resolves the same way: once the nodes behind it have been measured.
  const fittedKey = useRef(fitViewKey)
  useEffect(() => {
    if (fitViewKey === fittedKey.current) {
      return
    }
    fittedKey.current = fitViewKey
    fitView(fitViewOptions)
  }, [fitViewKey, fitView, fitViewOptions])

  // Frame the previewed layout; the RF nodes are rebuilt from it first, hence the short delay
  const previewedPlan = previewPlan
  useEffect(() => {
    if (!previewedPlan) {
      return
    }
    const timeout = window.setTimeout(() => void fitView({ ...fitViewOptions, duration: 300 }), 50)

    return () => window.clearTimeout(timeout)
  }, [previewedPlan, fitView, fitViewOptions])

  // Viewport → host (canvasZoom/canvasPan + viewportTransform): the
  // effector canvas stores position the Angular AI overlays from it. onMove covers
  // pans, zooms and programmatic moves; onInit reports the initial (fitted) viewport.
  const reportViewportMove = useCallback(
    (_event: unknown, viewport: Viewport) => onViewportChange(toLogicalViewport(viewport)),
    [onViewportChange, toLogicalViewport]
  )

  const onCanvasInit = useCallback(() => {
    applyPanTarget()
    onViewportChange(toLogicalViewport(getViewport()))
  }, [applyPanTarget, onViewportChange, getViewport, toLogicalViewport])

  const withBoundaryChildren = useCallback(
    (rfIds: Iterable<string>): Set<string> => {
      const base = new Set(rfIds)

      return new Set([
        ...base,
        ...nodes
          .filter((node) => node.type === BOUNDARY_EVENT_NODE_TYPE && base.has(node.parentId))
          .map((node) => node.id),
      ])
    },
    [nodes]
  )

  const updateLiveDragRects = useCallback(
    (draggedNodes: Node[], interaction: NodeSequenceDrag) => {
      interaction.liveRects = computeLiveDragRects({
        draggedNodes,
        followingBoundaries: nodes.filter(
          (node) =>
            node.type === BOUNDARY_EVENT_NODE_TYPE &&
            interaction.actualDraggedIds.has(node.parentId)
        ),
        originalRects: interaction.nodeRects,
        fallbackRect: internalRect,
      })
    },
    [internalRect, nodes]
  )

  const previewDraggedSequences = useCallback(
    (draggedNodes: Node[]) => {
      const interaction = nodeSequenceDrag.current
      if (!interaction) {
        return
      }
      updateLiveDragRects(draggedNodes, interaction)
      setEdges((previous) =>
        previous.map((edge) => {
          const origin = interaction.origins.get(edge.id)
          if (!origin) {
            return edge
          }
          const sourceRect = interaction.endpointIds.has(edge.source)
            ? (interaction.liveRects.get(edge.source) ?? origin.sourceRect)
            : origin.sourceRect
          const targetRect = interaction.endpointIds.has(edge.target)
            ? (interaction.liveRects.get(edge.target) ?? origin.targetRect)
            : origin.targetRect
          const points = routeForEndpointRects(origin, sourceRect, targetRect)

          return { ...edge, data: { ...edge.data, points } }
        })
      )
    },
    [setEdges, updateLiveDragRects]
  )

  const finishNodeSequenceDrag = useCallback(
    (movedRfIds: Iterable<string>, rectOverrides: Map<string, EdgeRect> = new Map()) => {
      const interaction = nodeSequenceDrag.current
      if (!interaction) {
        return
      }
      const movedEndpointIds = withBoundaryChildren(movedRfIds)
      const finalPoints = new Map<string, Point[]>()
      const updates: Parameters<typeof onSequencesChanged>[0] = []

      const canonicalNodeIds = new Set(graphRef.current.nodes.map((node) => node.id))
      const canonicalEdgeIds = new Set(graphRef.current.edges.map((edge) => edge.id))
      interaction.origins.forEach((origin, edgeId) => {
        if (!canonicalEdgeIds.has(edgeId)) {
          return
        }
        const { edge } = origin
        const sourceRect = movedEndpointIds.has(edge.source)
          ? (rectOverrides.get(edge.source) ??
            interaction.liveRects.get(edge.source) ??
            internalRect(edge.source) ??
            origin.sourceRect)
          : origin.sourceRect
        const targetRect = movedEndpointIds.has(edge.target)
          ? (rectOverrides.get(edge.target) ??
            interaction.liveRects.get(edge.target) ??
            internalRect(edge.target) ??
            origin.targetRect)
          : origin.targetRect
        const points = routeForEndpointRects(origin, sourceRect, targetRect)
        finalPoints.set(edgeId, points)
        if (!pointsEqual(points, origin.points)) {
          holdSequencePointsUntilCanonical(edgeId, points)
          updates.push({ sequence: origin.edge.data.sequence, points })
        }
      })

      nodeSequenceDrag.current = null
      setNodes((previous) =>
        previous.filter(
          (node) => !interaction.draggedIds.has(node.id) || canonicalNodeIds.has(node.id)
        )
      )
      setEdges((previous) =>
        previous
          .filter((edge) => !interaction.origins.has(edge.id) || canonicalEdgeIds.has(edge.id))
          .map((edge) => {
            const points = finalPoints.get(edge.id)

            return points ? { ...edge, data: { ...edge.data, points } } : edge
          })
      )
      if (updates.length) {
        onSequencesChanged(updates)
      }
    },
    [
      holdSequencePointsUntilCanonical,
      internalRect,
      onSequencesChanged,
      setEdges,
      setNodes,
      withBoundaryChildren,
    ]
  )

  const cancelNodeSequenceDrag = useCallback(() => {
    const interaction = nodeSequenceDrag.current
    if (!interaction) {
      return
    }
    nodeSequenceDrag.current = null
    cancelledNodeDragIds.current = new Set(interaction.actualDraggedIds)
    const canonicalNodes = new Map(graphRef.current.nodes.map((node) => [node.id, node]))
    const canonicalEdges = new Map(graphRef.current.edges.map((edge) => [edge.id, edge]))
    setNodes((previous) =>
      previous
        .filter((node) => !interaction.draggedIds.has(node.id) || canonicalNodes.has(node.id))
        .map((node) => {
          if (!interaction.draggedIds.has(node.id)) {
            return node
          }
          const canonical = canonicalNodes.get(node.id)
          const pending = pendingNodeLayouts.current.get(node.id)

          return canonical
            ? {
                ...canonical,
                ...(node.measured?.width ? { measured: node.measured } : {}),
                ...(node.selected ? { selected: true } : {}),
                ...(pending ? { position: pending.position, parentId: pending.parentId } : {}),
              }
            : node
        })
    )
    setEdges((previous) =>
      previous
        .filter((edge) => !interaction.origins.has(edge.id) || canonicalEdges.has(edge.id))
        .map((edge) => {
          if (!interaction.origins.has(edge.id)) {
            return edge
          }
          const canonical = canonicalEdges.get(edge.id)
          const pending = pendingSequencePoints.current.get(edge.id)

          return canonical
            ? {
                ...canonical,
                ...(edge.selected ? { selected: true } : {}),
                ...(pending ? { data: { ...canonical.data, points: pending } } : {}),
              }
            : edge
        })
    )
  }, [setEdges, setNodes])

  useEffect(() => {
    const cancelIfUnhandled = () => {
      if (!nodeSequenceDrag.current?.stopHandled) {
        cancelNodeSequenceDrag()
      }
    }
    const cancelAfterUnhandledPointerUp = () => {
      const timeout = window.setTimeout(() => {
        pendingCommitTimeouts.current.delete(timeout)
        cancelIfUnhandled()
      })
      pendingCommitTimeouts.current.add(timeout)
    }
    window.addEventListener('pointerup', cancelAfterUnhandledPointerUp)
    window.addEventListener('pointercancel', cancelIfUnhandled)
    window.addEventListener('blur', cancelIfUnhandled)

    return () => {
      window.removeEventListener('pointerup', cancelAfterUnhandledPointerUp)
      window.removeEventListener('pointercancel', cancelIfUnhandled)
      window.removeEventListener('blur', cancelIfUnhandled)
    }
  }, [cancelNodeSequenceDrag])

  useEffect(() => {
    if (readonly) {
      cancelNodeSequenceDrag()
    }
  }, [readonly, cancelNodeSequenceDrag])

  const onNodeDragStart = useCallback(
    (_event: React.MouseEvent, _node: Node, draggedNodes: Node[]) => {
      cancelledNodeDragIds.current.clear()
      onCanvasInteraction()
      const draggedIds = new Set(draggedNodes.map((node) => node.id))
      const endpointIds = withBoundaryChildren(draggedIds)
      const nodeRects = new Map<string, EdgeRect>()
      const rectIds = new Set([...draggedIds, ...endpointIds])
      nodes.forEach((node) => {
        if (rectIds.has(node.id) && node.parentId) {
          rectIds.add(node.parentId)
        }
      })
      rectIds.forEach((nodeId) => {
        const rect = internalRect(nodeId)
        if (rect) {
          nodeRects.set(nodeId, rect)
        }
      })
      const origins = new Map<string, SequenceDragOrigin>()
      edges.forEach((edge) => {
        if (
          edge.type !== SEQUENCE_EDGE_TYPE ||
          (!endpointIds.has(edge.source) && !endpointIds.has(edge.target))
        ) {
          return
        }
        const sequenceEdge = edge as Edge<SequenceEdgeData>
        const edgeData = sequenceEdge.data
        origins.set(edge.id, {
          edge: sequenceEdge,
          points: edgeData.points,
          sourceRect: internalRect(edge.source) ?? edgeData.sourceRect,
          targetRect: internalRect(edge.target) ?? edgeData.targetRect,
        })
      })
      // Boundary children move with a dragged parent and must be protected from
      // inbound prop snapshots for the same interaction window.
      nodeSequenceDrag.current = {
        draggedIds: endpointIds,
        actualDraggedIds: draggedIds,
        endpointIds,
        nodeRects,
        liveRects: new Map(nodeRects),
        origins,
        stopHandled: false,
      }
    },
    [edges, internalRect, nodes, onCanvasInteraction, withBoundaryChildren]
  )

  // Live nav-area stretching: recompute the padded member bounding box inside the
  // drag's own state pass (Selection-Grouping-style) — a store round-trip per drag
  // tick re-syncs the whole nodes array and visibly stutters.
  const onNodeDrag = useCallback(
    (_event: React.MouseEvent, _node: Node, draggedNodes: Node[]) => {
      const snappedDraggedNodes = snapDraggedNodesToGrid(draggedNodes)
      applyLocalDraggedPositions(snappedDraggedNodes)
      previewDraggedSequences(snappedDraggedNodes)
      const dragsAreaMember = snappedDraggedNodes.some(
        (dragged) =>
          dragged.type === BPMN_NODE_TYPE &&
          !!(dragged.data as BpmnNodeData).definition.navigationAreaId
      )
      if (dragsAreaMember) {
        setNodes((previous) => stretchNavAreaNodes(previous, getNodeDimensions))
      }

      // Nodes turn red live while hovering an invalid spot
      // (same hasValidPosition() check the drop handler applies on release)
      const draggedBpmnNodes = snappedDraggedNodes.filter(
        (dragged) => dragged.type === BPMN_NODE_TYPE
      )
      const draggedNodeIds = new Set(
        draggedBpmnNodes.map((dragged) => (dragged.data as BpmnNodeData).definition.id)
      )
      const invalidByRfId = new Map(
        draggedBpmnNodes
          .map((dragged) => {
            const definition = (dragged.data as BpmnNodeData).definition
            const currentLane = data.swimlanes.find(
              (swimlane) => swimlane.id === definition.swimlaneDefinitionId
            )
            if (!currentLane) {
              return null
            }
            const nodeRect = {
              ...toAbsolutePosition(dragged.position, currentLane),
              ...getNodeDimensions(definition.type),
            }

            return [
              dragged.id,
              !isRectInsideSomeLane(nodeRect, data.swimlanes) ||
                overlapsAnyNode(nodeRect, draggedNodeIds, data, getNodeDimensions),
            ] as const
          })
          .filter(Boolean)
      )
      const invalidityChanged = nodes.some(
        (node) =>
          invalidByRfId.has(node.id) &&
          !!(node.data as BpmnNodeData).invalid !== invalidByRfId.get(node.id)
      )
      if (invalidityChanged) {
        setNodes((previous) =>
          previous.map((node) =>
            invalidByRfId.has(node.id) &&
            !!(node.data as BpmnNodeData).invalid !== invalidByRfId.get(node.id)
              ? { ...node, data: { ...node.data, invalid: invalidByRfId.get(node.id) } }
              : node
          )
        )
      }
    },
    [
      applyLocalDraggedPositions,
      data,
      nodes,
      previewDraggedSequences,
      setNodes,
      snapDraggedNodesToGrid,
    ]
  )

  const handleBoundaryEventDragStop = useCallback(
    (boundaryNode: Node) => {
      const { boundaryEvent } = boundaryNode.data as BoundaryEventNodeData
      const parentDefinition = data.nodes.find(
        (definition) => definition.id === boundaryEvent.nodeDefinitionId
      )
      if (!parentDefinition) {
        cancelNodeSequenceDrag()

        return
      }
      const snapped = snapToNodeBorder(boundaryNode.position, parentDefinition, BOUNDARY_DIMENSIONS)
      setNodes((previous) =>
        previous.map((node) =>
          node.id === boundaryNode.id ? { ...node, position: snapped } : node
        )
      )
      const parentRect = internalRect(toNodeId(parentDefinition.id))
      const boundaryId = toBoundaryId(boundaryEvent.id)
      const rectOverrides = new Map<string, EdgeRect>()
      if (parentRect) {
        rectOverrides.set(boundaryId, {
          x: parentRect.x + snapped.x,
          y: parentRect.y + snapped.y,
          ...getNodeDimensions(boundaryEvent.type),
        })
      }
      const original = boundaryEvent.layoutOptions ?? { x: 0, y: 0 }
      const moved = snapped.x !== original.x || snapped.y !== original.y
      if (moved) {
        holdNodeLayoutUntilCanonical(boundaryId, {
          position: snapped,
          parentId: toNodeId(parentDefinition.id),
        })
      }
      finishNodeSequenceDrag(moved ? [boundaryId] : [], rectOverrides)
      onBoundaryEventPositionChanged({
        boundaryEvent,
        updates: { layoutOptions: snapped },
      })
    },
    [
      cancelNodeSequenceDrag,
      data.nodes,
      finishNodeSequenceDrag,
      holdNodeLayoutUntilCanonical,
      internalRect,
      setNodes,
      onBoundaryEventPositionChanged,
    ]
  )

  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: Node, draggedNodes: Node[]) => {
      const snappedDraggedNodes = snapDraggedNodesToGrid(draggedNodes)
      const snappedNode = snappedDraggedNodes.find((candidate) => candidate.id === node.id) ?? node
      applyLocalDraggedPositions(snappedDraggedNodes)
      previewDraggedSequences(snappedDraggedNodes)
      if (cancelledNodeDragIds.current.has(snappedNode.id)) {
        cancelledNodeDragIds.current.clear()

        return
      }
      if (
        readonly ||
        !graphRef.current.nodes.some((candidate) => candidate.id === snappedNode.id)
      ) {
        cancelNodeSequenceDrag()

        return
      }
      if (nodeSequenceDrag.current) {
        nodeSequenceDrag.current.stopHandled = true
      }
      if (snappedDraggedNodes.length === 1 && snappedNode.type === BOUNDARY_EVENT_NODE_TYPE) {
        handleBoundaryEventDragStop(snappedNode)

        return
      }

      const canonicalNodeIds = new Set(graphRef.current.nodes.map((candidate) => candidate.id))
      const draggedBpmnNodes = snappedDraggedNodes.filter(
        (dragged) => dragged.type === BPMN_NODE_TYPE && canonicalNodeIds.has(dragged.id)
      )
      const plan = planNodeDrop(
        draggedBpmnNodes.map((dragged) => ({
          id: dragged.id,
          position: dragged.position,
          definition: (dragged.data as BpmnNodeData).definition,
        })),
        data,
        getNodeDimensions
      )

      // Drop the live drag tint: a committed drop sits on a valid spot, and a
      // node that snapped back goes back to the mapper's verdict — a pasted node
      // still standing on another one stays red instead of losing the warning to
      // the drag that never landed.
      const mappedInvalid = new Map(
        graph.nodes.map((node) => [node.id, !!(node.data as BpmnNodeData).invalid])
      )
      const snappedBack = new Set(plan.snappedBackRfIds)
      const draggedRfIds = new Set(draggedBpmnNodes.map((dragged) => dragged.id))
      setNodes((previous) =>
        previous.map((node) => {
          const invalid = snappedBack.has(node.id) && !!mappedInvalid.get(node.id)

          return draggedRfIds.has(node.id) && !!(node.data as BpmnNodeData).invalid !== invalid
            ? { ...node, data: { ...node.data, invalid } }
            : node
        })
      )

      const applyDragOutcome = (outcome: NodeDropCommit) => {
        const movedIds = new Set(outcome.movedRfIds)
        draggedBpmnNodes.forEach((dragged) => {
          if (!movedIds.has(dragged.id)) {
            return
          }
          const reparent = outcome.localReparents.find((update) => update.id === dragged.id)
          holdNodeLayoutUntilCanonical(dragged.id, {
            position: reparent?.position ?? dragged.position,
            parentId: reparent?.parentId ?? dragged.parentId,
          })
        })
        if (outcome.localReparents.length) {
          setNodes((previous) =>
            stretchNavAreaNodes(
              previous.map((node) => {
                const reparent = outcome.localReparents.find((update) => update.id === node.id)

                return reparent
                  ? { ...node, parentId: reparent.parentId, position: reparent.position }
                  : node
              }),
              getNodeDimensions
            )
          )
        }
        if (plan.positionUpdates.length) {
          onNodesPositionChanged(plan.positionUpdates)
        }
        if (outcome.swimlaneUpdates.length) {
          onNodesSwimlaneChanged(outcome.swimlaneUpdates)
        }

        // Single store sync per drag (not per tick): the effector areas$ re-decorates
        // from the FINAL positions so the props-driven rects converge with the local
        // stretch (reverted drags converge back to the original rect).
        const navAreaUpdates = draggedBpmnNodes
          .map((dragged) => ({
            definition: (dragged.data as BpmnNodeData).definition,
            finalPosition:
              outcome.localReparents.find((update) => update.id === dragged.id)?.position ??
              dragged.position,
          }))
          .filter(({ definition }) => !!definition.navigationAreaId)
          .map(({ definition, finalPosition }) => ({
            nodeId: definition.id,
            navigationAreaId: definition.navigationAreaId,
            swimlaneDefinitionId: definition.swimlaneDefinitionId,
            position: finalPosition,
          }))
        if (navAreaUpdates.length) {
          onNodesDragging(navAreaUpdates)
        }

        finishNodeSequenceDrag(outcome.movedRfIds)
      }

      // Moving a node that is
      // assigned to a navigation area out of its swimlane needs confirmation, and
      // cancelling reverts every cross-lane node from this drag.
      if (!requiresAreaRemovalConfirm(plan)) {
        applyDragOutcome(commitNodeDrop(plan))

        return
      }
      onConfirmAreaRemoval()
        .then((confirmed) => {
          applyDragOutcome(confirmed ? commitNodeDrop(plan) : revertCrossLaneNodeDrop(plan))
        })
        .catch(cancelNodeSequenceDrag)
    },
    [
      readonly,
      applyLocalDraggedPositions,
      cancelNodeSequenceDrag,
      data,
      graph,
      handleBoundaryEventDragStop,
      setNodes,
      onNodesPositionChanged,
      onNodesSwimlaneChanged,
      onNodesDragging,
      onConfirmAreaRemoval,
      finishNodeSequenceDrag,
      holdNodeLayoutUntilCanonical,
      previewDraggedSequences,
      snapDraggedNodesToGrid,
    ]
  )

  // React Flow routes drags that start on its selection bounding box through a
  // separate callback family. Reuse the exact node-drag pipeline so group moves
  // keep grid snapping, live sequence routing, validation and persistence.
  const onSelectionDragStart = useCallback(
    (event: React.MouseEvent, selectedNodes: Node[]) => {
      if (selectedNodes.length) {
        onNodeDragStart(event, selectedNodes[0], selectedNodes)
      }
    },
    [onNodeDragStart]
  )
  const onSelectionDrag = useCallback(
    (event: React.MouseEvent, selectedNodes: Node[]) => {
      if (selectedNodes.length) {
        onNodeDrag(event, selectedNodes[0], selectedNodes)
      }
    },
    [onNodeDrag]
  )
  const onSelectionDragStop = useCallback(
    (event: React.MouseEvent, selectedNodes: Node[]) => {
      if (selectedNodes.length) {
        onNodeDragStop(event, selectedNodes[0], selectedNodes)
      }
    },
    [onNodeDragStop]
  )

  // The node's bounding rect in canvas-container pixels, used to anchor the
  // floating actions menu below the node.
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      // A layout preview shows a diagram that is not saved: nothing on it can be configured
      if (layoutPreviewOpen) {
        return
      }
      // React Flow has already toggled the node into/out of its local selection
      // before this callback. Do not turn an additive Cmd/Ctrl click into a normal
      // node click: that would open the config panel and clear the Effector
      // multiselection that onSelectionChange is about to publish.
      if (event.metaKey || event.ctrlKey) {
        setSelectedLaneId(null)
        onCanvasInteraction()

        return
      }
      setSelectedLaneId(null)
      if (node.type === BPMN_NODE_TYPE) {
        onOpenConfigPanel((node.data as BpmnNodeData).definition, {
          type: 'node',
          viewportCoords: nodeViewportRect(event, cssZoom),
        })
      }
      if (node.type === BOUNDARY_EVENT_NODE_TYPE) {
        onOpenConfigPanel((node.data as BoundaryEventNodeData).boundaryEvent, {
          type: 'boundaryEvent',
          viewportCoords: nodeViewportRect(event, cssZoom),
        })
      }
    },
    [onCanvasInteraction, onOpenConfigPanel, cssZoom, layoutPreviewOpen]
  )

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault()
      if (layoutPreviewOpen) {
        return
      }
      if (node.type === BPMN_NODE_TYPE) {
        onNodeRightClick((node.data as BpmnNodeData).definition, {
          type: 'node',
          viewportCoords: nodeViewportRect(event, cssZoom),
        })
      }
      if (node.type === BOUNDARY_EVENT_NODE_TYPE) {
        onNodeRightClick((node.data as BoundaryEventNodeData).boundaryEvent, {
          type: 'boundaryEvent',
          viewportCoords: nodeViewportRect(event, cssZoom),
        })
      }
    },
    [onNodeRightClick, cssZoom, layoutPreviewOpen]
  )

  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (edge.type !== SEQUENCE_EDGE_TYPE || !edge.data || layoutPreviewOpen) {
        return
      }
      // Sequences stay locked while the additive-selection modifier is held.
      if (event.metaKey || event.ctrlKey) {
        onCanvasInteraction()

        return
      }
      setSelectedLaneId(null)
      onSequenceClick({
        sequence: (edge.data as SequenceEdgeData).sequence,
        viewportCoords: pointerViewportPoint(event, cssZoom),
      })
    },
    [onCanvasInteraction, onSequenceClick, cssZoom, layoutPreviewOpen]
  )

  // A nav area's bounding rect in canvas-container pixels — anchors the floating
  // actions menu below the rect.
  const navAreaViewportCoords = useCallback(
    (event: React.MouseEvent, area: NavAreaRender, lane: SwimlaneSummary) => {
      const absolute = toAbsolutePosition(
        { x: area.layoutOptions.x, y: area.layoutOptions.y },
        lane
      )
      const topLeft = flowToScreenPosition(absolute)
      const bottomRight = flowToScreenPosition({
        x: absolute.x + area.layoutOptions.width,
        y: absolute.y + area.layoutOptions.height,
      })

      return projectedViewportRect(canvasContainerRect(event), topLeft, bottomRight, cssZoom)
    },
    [flowToScreenPosition, cssZoom]
  )

  // Swimlane bodies are transparent to pointer events (so panning works through
  // them), which means clicks on empty lane surface land on the pane — hit-test
  // them against the lanes for click-to-select-lane. Nav-area overlays are
  // pointer-transparent too; when the click lands inside one, select the area
  // rather than the lane.
  const onPaneClick = useCallback(
    (event: React.MouseEvent) => {
      dismissNewNodePanelOnPaneClick()
      const flowPoint = screenToFlowPosition({ x: event.clientX, y: event.clientY })
      const lane = findSwimlaneAt(flowPoint, data.swimlanes)
      const hitArea =
        lane && lane.id === navAreasLaneId && !layoutPreviewOpen
          ? findNavAreaAt(flowPoint, navAreas ?? [], lane)
          : null
      if (hitArea) {
        onNavAreaClick({
          area: hitArea,
          viewportCoords: navAreaViewportCoords(event, hitArea, lane),
        })

        return
      }
      setSelectedLaneId(layoutPreviewOpen ? null : (lane?.id ?? null))
      onPaneClicked({ lane, flowPoint })
    },
    [
      dismissNewNodePanelOnPaneClick,
      onPaneClicked,
      onNavAreaClick,
      screenToFlowPosition,
      data.swimlanes,
      navAreas,
      navAreasLaneId,
      navAreaViewportCoords,
      layoutPreviewOpen,
    ]
  )

  // Right-click on empty lane surface → Paste floating menu (swimlaneRightClick)
  const onPaneContextMenu = useCallback(
    (event: React.MouseEvent | MouseEvent) => {
      event.preventDefault()
      const reactEvent = event as React.MouseEvent
      const flowPoint = screenToFlowPosition({ x: reactEvent.clientX, y: reactEvent.clientY })
      const lane = findSwimlaneAt(flowPoint, data.swimlanes)
      if (lane) {
        onSwimlaneRightClick({
          swimlane: lane,
          flowPoint,
          viewportCoords: pointerViewportPoint(reactEvent, cssZoom),
        })
      }
    },
    [screenToFlowPosition, data.swimlanes, onSwimlaneRightClick, cssZoom]
  )

  // React Flow's selection-change hook covers both modifier-drag rectangles and
  // additive Cmd/Ctrl clicks. Only BPMN nodes belong in a multiselection, so
  // prune boundary events and RF's automatically selected connected edges.
  const onSelectionChange = useCallback(
    ({ nodes: selectedNodes, edges: selectedEdges }: OnSelectionChangeParams) => {
      const selectionPlan = planBpmnMultiselection(
        selectedNodes,
        selectedEdges.length,
        BPMN_NODE_TYPE
      )
      const selectedBpmnNodes = selectionPlan.bpmnNodes

      if (selectionPlan.pruneNonBpmnNodes || selectionPlan.clearSelectedEdges) {
        const retainedIds = new Set(selectedBpmnNodes.map((node) => node.id))
        if (selectionPlan.pruneNonBpmnNodes) {
          setNodes((previous) =>
            previous.map((node) =>
              node.selected && !retainedIds.has(node.id) ? { ...node, selected: false } : node
            )
          )
        }
        if (selectionPlan.clearSelectedEdges) {
          setEdges((previous) =>
            previous.map((edge) => (edge.selected ? { ...edge, selected: false } : edge))
          )
        }

        // The controlled selection callback re-enters with the pruned result.
        return
      }

      // React Flow's own selection model already covers a single node, so the
      // multiselect action state is only published from two nodes up.
      if (!selectionPlan.reportMultiselection) {
        if (multiselectionReported.current) {
          multiselectionReported.current = false
          onMultiSelectionCleared()
        }

        return
      }

      const rects = selectedBpmnNodes.map((node) => internalRect(node.id)).filter(Boolean)
      const containerRect = flowStoreApi.getState().domNode?.getBoundingClientRect()
      if (rects.length !== selectedBpmnNodes.length || !containerRect) {
        return
      }
      const left = Math.min(...rects.map((rect) => rect.x))
      const top = Math.min(...rects.map((rect) => rect.y))
      const right = Math.max(...rects.map((rect) => rect.x + rect.width))
      const bottom = Math.max(...rects.map((rect) => rect.y + rect.height))
      const screenTopLeft = flowToScreenPosition({ x: left, y: top })
      const screenBottomRight = flowToScreenPosition({ x: right, y: bottom })

      multiselectionReported.current = true
      onMultiSelection({
        nodes: selectedBpmnNodes.map((node) => (node.data as BpmnNodeData).definition),
        viewportCoords: projectedViewportRect(
          containerRect,
          screenTopLeft,
          screenBottomRight,
          cssZoom
        ),
        clearSelection: () =>
          setNodes((previous) =>
            previous.map((node) => (node.selected ? { ...node, selected: false } : node))
          ),
      })
    },
    [
      flowStoreApi,
      flowToScreenPosition,
      cssZoom,
      internalRect,
      onMultiSelection,
      onMultiSelectionCleared,
      setEdges,
      setNodes,
    ]
  )

  const isValidConnection = useCallback(
    (connection: Connection | Edge): boolean => {
      if (readonly || !connection.source || !connection.target) {
        return false
      }

      return validateSequenceConnection(
        { source: connection.source, target: connection.target },
        data
      ).valid
    },
    [readonly, data]
  )

  const createValidatedSequence = useCallback(
    (candidate: { source: string; target: string; targetPosition?: Position }): void => {
      const validation = validateSequenceConnection(candidate, data)
      if (validation.valid === false) {
        onDropError(sequenceValidationMessage(validation.reason))

        return
      }
      const payload = withInitialSequencePoints(validation.payload, data, candidate.targetPosition)
      if (payload) {
        onSequenceCreate(payload)
      } else {
        onDropError('Could not create the connection, please try again')
      }
    },
    [data, onDropError, onSequenceCreate]
  )

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!readonly && connection.source && connection.target) {
        createValidatedSequence({
          source: connection.source,
          target: connection.target,
          targetPosition: targetHandlePosition(connection.targetHandle),
        })
      }
    },
    [readonly, createValidatedSequence]
  )

  /**
   * The node (and port) a pointer over the canvas would connect to. The
   * connection preview and the drop below share it, so the arrow cannot land
   * anywhere other than where the created sequence attaches.
   */
  const resolveTarget = useCallback<ConnectionTargetResolver>(
    (pointer, sourceRfId) => resolveConnectionTarget(pointer, sourceRfId, data),
    [data]
  )

  // Dropping a connection anywhere on a node still creates the
  // sequence. React Flow only snaps within connectionRadius of a target handle,
  // which leaves most of a 140px-wide activity node dead — so hit-test the drop
  // point against the node rects and connect to the node under the cursor, at
  // the port nearest to it.
  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent, connectionState: FinalConnectionState) => {
      if (readonly || connectionState.isValid || !connectionState.fromNode) {
        return
      }
      // connectionState.to is in container px here (only useConnection converts
      // it) — project the drop point from the event instead.
      const { clientX, clientY } = 'changedTouches' in event ? event.changedTouches[0] : event
      const dropPoint = screenToFlowPosition({ x: clientX, y: clientY })
      const target = resolveTarget(dropPoint, connectionState.fromNode.id)
      if (!target) {
        // Dropped on empty lane space → open the New Node panel at the same
        // center-grid point where its selected node will be created. Outside
        // every lane no BPMN node can be created, so the drop stays a no-op.
        const panelPosition = snapPointToGrid(dropPoint, GRID_SNAP_SIZE)
        if (findSwimlaneAt(panelPosition, data.swimlanes)) {
          const source = resolveSequenceSource(connectionState.fromNode.id, data)
          if (source && !canAddOutgoingSequence(source, data.sequences)) {
            onDropError('This node already has an outgoing sequence')

            return
          }
          openNewNodePanel({
            position: panelPosition,
            sourceRfId: connectionState.fromNode.id,
          })
        }

        return
      }
      createValidatedSequence({
        source: connectionState.fromNode.id,
        target: target.rfId,
        targetPosition: target.position,
      })
    },
    [
      readonly,
      data,
      screenToFlowPosition,
      resolveTarget,
      createValidatedSequence,
      openNewNodePanel,
      onDropError,
    ]
  )

  const { palettePreview, dragOverEdgeId, onDragOver, onDragLeave, onDrop } = usePaletteDnd({
    data,
    edges,
    readonly,
    paletteDragType,
    screenToFlowPosition,
    onPaletteDrop,
    onNodeDrop,
    onSequenceDrop,
    onDropError,
  })

  return (
    <CssZoomContext.Provider value={cssZoom}>
      <SwimlaneUiContext.Provider value={swimlaneUi}>
        <EditingUsersContext.Provider value={editingUsersByNode}>
          <DragOverEdgeContext.Provider value={dragOverEdgeId}>
            <ConnectionTargetContext.Provider value={resolveTarget}>
              <ReactFlow
                ref={reactFlowRef}
                className={cssZoom === 1 ? undefined : 'bpmn-react-flow-css-zoom-compensated'}
                style={reactFlowStyle}
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeDragStart={onNodeDragStart}
                onNodeDrag={onNodeDrag}
                onNodeDragStop={onNodeDragStop}
                onSelectionDragStart={onSelectionDragStart}
                onSelectionDrag={onSelectionDrag}
                onSelectionDragStop={onSelectionDragStop}
                onNodeClick={onNodeClick}
                onNodeContextMenu={onNodeContextMenu}
                onEdgeClick={onEdgeClick}
                onPaneClick={onPaneClick}
                onPaneContextMenu={onPaneContextMenu}
                onSelectionStart={onCanvasInteraction}
                onSelectionChange={onSelectionChange}
                onMoveStart={onCanvasInteraction}
                onMove={reportViewportMove}
                onInit={onCanvasInit}
                onConnect={onConnect}
                onConnectEnd={onConnectEnd}
                onConnectStart={onCanvasInteraction}
                isValidConnection={isValidConnection}
                connectionLineComponent={SequenceConnectionLine}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                minZoom={MIN_ZOOM * cssZoom}
                maxZoom={MAX_ZOOM * cssZoom}
                fitView
                fitViewOptions={fitViewOptions}
                // BPMN drag callbacks snap visual/group centers to the 10px grid.
                // RF's built-in snapping uses top-left corners, which puts 90px
                // activities and 40px gateways on incompatible center rows.
                zoomOnDoubleClick={false}
                deleteKeyCode={[]}
                // Rectangle-selection mode for Shift and either platform
                // modifier; additive node clicks use Cmd/Ctrl.
                selectionKeyCode={['Shift', 'Meta', 'Control']}
                multiSelectionKeyCode={['Meta', 'Control']}
                selectionMode={SelectionMode.Partial}
                // Wide catch radius so a connection drag anywhere over the target
                // node still snaps to its left-border dot handle. Default is 20px,
                // which only covers a sliver around the small dot handle.
                connectionRadius={80}
                // A bare click on the connect-source handle arms React Flow's
                // click-to-connect mode; that state strips connectionindicator
                // from every source handle (they lose pointer-events: all) and
                // only a target handle can clear it — but ours are visibility:
                // hidden until a drag is in progress, so the state gets stuck
                // and drag-to-connect is dead until a refresh. Drag is the only
                // gesture we support anyway, so turn click-to-connect off.
                connectOnClick={false}
                nodesConnectable={!readonly}
                nodesDraggable={!readonly}
                elementsSelectable={!layoutPreviewOpen}
                proOptions={{ hideAttribution: true }}
                data-testid="bpmn-react-flow-canvas"
              >
                <Background
                  variant={BackgroundVariant.Dots}
                  color={GraphColors.neutral_100}
                  gap={20}
                  size={2}
                />
                <div data-testid="bpmn-canvas-controls">
                  <Controls
                    position="bottom-right"
                    showInteractive={false}
                    style={CANVAS_CONTROLS_STYLE}
                  />
                </div>
                <Panel
                  position="bottom-right"
                  className="react-flow__controls"
                  style={LAYOUT_TOOLS_PANEL_STYLE}
                  data-testid="bpmn-canvas-layout-tools"
                >
                  <ControlButton
                    onClick={autoArrange}
                    className={LAYOUT_TOOL_BUTTON_CLASS_NAME}
                    disabled={liveReadonly || arranging || !!layoutPreview}
                    title={liveReadonly ? 'Arrange is unavailable in read-only mode' : 'Arrange'}
                    aria-label="Arrange"
                    data-testid="bpmn-canvas-auto-arrange"
                  >
                    <i className="ph ph-align-left text-lg"></i>
                  </ControlButton>
                </Panel>
                {layoutPreview && previewPlan && (
                  <LayoutPreviewBar
                    summary={layoutPreviewSummary(
                      {
                        nodes: previewPlan.nodes.length,
                        lanes: previewPlan.swimlanes.length,
                        sequences: previewPlan.sequences.length,
                      },
                      {
                        stale: previewStale,
                        applying,
                        planning: arranging,
                      }
                    )}
                    showCurrent={showCurrent}
                    stale={previewStale}
                    busy={applying || arranging || confirming}
                    canApply={!isEmptyLayoutPlan(previewPlan)}
                    onToggleShowCurrent={() => setShowCurrent((current) => !current)}
                    onCancel={cancelLayoutPreview}
                    onApply={confirmLayoutPreview}
                    onRefresh={() => void planLayoutPreview(true)}
                    testId="bpmn-canvas-layout-preview"
                  />
                )}
                {paletteDragType && palettePreview && (
                  <PaletteDropPreview
                    type={paletteDragType}
                    preview={palettePreview}
                  />
                )}
              </ReactFlow>
            </ConnectionTargetContext.Provider>
          </DragOverEdgeContext.Provider>
        </EditingUsersContext.Provider>
      </SwimlaneUiContext.Provider>
    </CssZoomContext.Provider>
  )
}
